import React from 'react';

import CircularProgress from '@material-ui/core/CircularProgress';
import { makeMuiStyles, useCommonStyles } from '@styles';
import clsx from 'clsx';
import Color from 'color';

const useStyles = makeMuiStyles((theme, common) => ({
  buttonContainer: {
    ...common.baseButton,
    ...common.flexCenter,
    ...common.middleText,
    whiteSpace: 'nowrap',
    padding: '12px 12px',
    transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
    borderRadius: 2,
    '&.color-secondary': {
      backgroundColor: theme.palette.secondary.main,
      color: theme.palette.secondary.contrastText,
    },
    '&.color-secondary:hover': {
      backgroundColor: Color(theme.palette.secondary.main).lighten(0.05).toString(),
    },
    '&.color-secondary:active': {
      backgroundColor: Color(theme.palette.secondary.main).darken(0.25).toString(),
    },
    '&.color-secondary:disabled': {
      ...common.baseButton,
      backgroundColor: `${theme.palette.secondary.light}`,
    },

    '&.color-primary': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    '&.color-primary:hover': {
      backgroundColor: Color(theme.palette.primary.main).lighten(0.05).toString(),
    },
    '&.color-primary:active': {
      backgroundColor: Color(theme.palette.primary.main).darken(0.25).toString(),
    },
    '&.color-primary:disabled': {
      ...common.baseButton,
      backgroundColor: Color(theme.palette.primary.main).alpha(0.4).toString(),
    },

    '&.color-accent': {
      background: `linear-gradient(180deg, ${theme.palette.accent.dark} 0%, ${theme.palette.accent.main} 100%)`,
      color: theme.palette.primary.contrastText,
    },
    '&.color-accent:hover': {
      background: `linear-gradient(180deg, ${Color(theme.palette.accent.dark)
        .lighten(0.05)
        .toString()} 0%, ${Color(theme.palette.accent.main).lighten(0.05).toString()} 100%)`,
    },
    '&.color-accent:active': {
      background: `linear-gradient(180deg, ${Color(theme.palette.accent.dark)
        .darken(0.25)
        .toString()} 0%, ${Color(theme.palette.accent.main).darken(0.25).toString()} 100%)`,
    },
    '&.color-accent:disabled': {
      ...common.baseButton,
      background: `linear-gradient(180deg, ${Color(theme.palette.accent.dark)
        .alpha(0.4)
        .toString()} 0%, ${Color(theme.palette.accent.main).alpha(0.4).toString()} 100%)`,
    },

    '&.color-accent-outlined': {
      color: theme.palette.accent.main,
      border: `1px solid ${theme.palette.accent.main}`,
    },
    '&.color-accent-outlined:hover': {
      color: Color(theme.palette.accent.main).lighten(0.25).toString(),
      border: `1px solid ${Color(theme.palette.accent.main).lighten(0.25).toString()}`,
    },
    '&.color-accent-outlined:active': {
      color: Color(theme.palette.accent.main).darken(0.25).toString(),
      border: `1px solid ${Color(theme.palette.accent.main).darken(0.25).toString()}`,
    },
    '&.color-accent-outlined:disabled': {
      ...common.baseButton,
      color: Color(theme.palette.accent.main).alpha(0.4).toString(),
      border: `1px solid ${Color(theme.palette.accent.main).alpha(0.4).toString()}`,
    },

    '&.color-warning': {
      backgroundColor: theme.palette.warning.main,
      color: theme.palette.warning.contrastText,
    },
    '&.color-warning:hover': {
      backgroundColor: Color(theme.palette.warning.main).lighten(0.05).toString(),
    },
    '&.color-warning:active': {
      backgroundColor: Color(theme.palette.warning.main).darken(0.25).toString(),
    },
    '&.color-warning:disabled': {
      ...common.baseButton,
      backgroundColor: Color(theme.palette.warning.main).alpha(0.4).toString(),
    },

    '&.color-outlined': {
      ...common.textPrimary,
      border: `1px solid ${theme.palette.primary.main}`,
      backgroundColor: theme.palette.background.paper,
    },
    '&.color-outlined:hover': {
      color: Color(theme.palette.primary.main).lighten(0.25).toString(),
      border: `1px solid ${Color(theme.palette.primary.main).lighten(0.25).toString()}`,
    },
    '&.color-outlined:active': {
      color: Color(theme.palette.primary.main).darken(0.25).toString(),
      border: `1px solid ${Color(theme.palette.primary.main).darken(0.25).toString()}`,
    },
    '&.color-outlined:disabled': {
      ...common.textGray500,
      border: `1px solid ${theme.palette.grey[500]}`,
    },

    '&.color-grey': {
      backgroundColor: theme.palette.grey[400],
      color: theme.palette.primary.contrastText,
    },
    '&.color-grey:hover': {
      backgroundColor: Color(theme.palette.grey[400]).lighten(0.1).toString(),
    },
    '&.color-grey:active': {
      backgroundColor: Color(theme.palette.grey[400]).darken(0.25).toString(),
    },
    '&.color-grey:disabled': {
      ...common.baseButton,
      backgroundColor: Color(theme.palette.grey[400]).alpha(0.7).toString(),
    },

    '&.color-default': {
      backgroundColor: theme.palette.primary.contrastText,
      color: theme.palette.primary.main,
    },
    '&.color-default:hover': {
      color: Color(theme.palette.primary.main).lighten(0.25).toString(),
    },

    '&.color-printable': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
      border: `1px solid ${theme.palette.grey[500]}`,
    },
    '&.color-printable:hover': {
      color: Color(theme.palette.text.primary).lighten(0.25).toString(),
    },

    '&.color-error': {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
    },
    '&.color-error:hover': {
      backgroundColor: Color(theme.palette.error.main).lighten(0.05).toString(),
    },
    '&.color-error:active': {
      backgroundColor: Color(theme.palette.error.main).darken(0.25).toString(),
    },
    '&.color-error:disabled': {
      ...common.baseButton,
      backgroundColor: Color(theme.palette.error.main).alpha(0.4).toString(),
    },

    '&.size-smaller': {
      padding: '0px 6px',
      height: 24,
    },
    '&.size-small': {
      padding: '0px 12px',
      height: 32,
    },
    '&.size-default': {
      padding: '0px 12px',
      height: 44,
    },
    '&.size-big': {
      padding: '0px 12px',
      height: 60,
    },

    '&.shape-round.size-small': {
      borderRadius: 16,
    },
    '&.shape-round.size-default': {
      borderRadius: 22,
    },
  },
}));

export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'grey'
  | 'custom'
  | 'default'
  | 'printable'
  | 'warning'
  | 'accent'
  | 'outlined'
  | 'accent-outlined'
  | 'error';

export type ButtonSize = 'default' | 'small' | 'big' | 'smaller';

export type ButtonShape = 'default' | 'round';

export interface ButtonProps
  extends Omit<React.HTMLProps<HTMLButtonElement>, 'color' | 'size' | 'ref'> {
  text: React.ReactNode;
  color: ButtonColor;
  size?: ButtonSize;
  shape?: ButtonShape;
  className?: string;
  isLoading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { text, color, className, shape, size = 'default', isLoading, ...rest } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <button
      ref={ref}
      className={clsx(
        classes.buttonContainer,
        `color-${color}`,
        className,
        `size-${size}`,
        shape && `shape-${shape}`,
      )}
      {...(rest as any)}
    >
      {isLoading && (
        <CircularProgress size={16} style={{ marginRight: '0.5rem' }} color="inherit" />
      )}
      {text}
    </button>
  );
});
